import { useState, useCallback, useRef, useEffect } from 'react';
import { FormattingState, defaultFormatting } from './FormattingToolbar';

export interface UseFormattingToolbarProps {
  initialFormatting?: Partial<FormattingState>;
  onFormattingChange?: (formatting: FormattingState) => void;
}

export function useFormattingToolbar({
  initialFormatting = {},
  onFormattingChange
}: UseFormattingToolbarProps = {}) {
  const [formatting, setFormatting] = useState<FormattingState>({
    ...defaultFormatting,
    ...initialFormatting
  });
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [activeField, setActiveField] = useState<string | null>(null);
  const activeSectionRef = useRef<HTMLElement | null>(null);

  const updateFormatting = useCallback((updates: Partial<FormattingState>) => {
    const newFormatting = { ...formatting, ...updates };
    setFormatting(newFormatting);
    onFormattingChange?.(newFormatting);
  }, [formatting, onFormattingChange]);

  const showFormattingToolbar = useCallback((
    fieldId: string,
    event: React.FocusEvent<HTMLElement> | HTMLElement,
    sectionSelector?: string
  ) => {
    setActiveField(fieldId);
    setShowToolbar(true);
    
    const element = 'target' in event ? event.target as HTMLElement : event;
    
    const sectionContainer = sectionSelector 
      ? element.closest(sectionSelector) as HTMLElement
      : element.closest('[data-section]') as HTMLElement;
    
    activeSectionRef.current = sectionContainer;
    
    if (sectionContainer) {
      const containerRect = sectionContainer.getBoundingClientRect();
      const parentContainer = sectionContainer.closest('.MuiBox-root, .MuiModal-root, .MuiPopover-root')?.getBoundingClientRect();
      
      if (parentContainer) {
        // Position toolbar centered underneath the specific section (header or footer) with some spacing
        const toolbarWidth = 600;
        const sectionType = sectionContainer.getAttribute('data-section');
        
        // Calculate base position relative to the parent container
        let calculatedTop = containerRect.bottom - parentContainer.top + 8;
        const calculatedLeft = Math.max(0, (containerRect.left + containerRect.width / 2) - parentContainer.left - (toolbarWidth / 2));
        
        // Add extra offset for footer to ensure it appears below header toolbar position
        if (sectionType === 'footer') {
          calculatedTop += 170; // Add extra space to push footer toolbar further down
        }
        
        setToolbarPosition({
          top: calculatedTop,
          left: calculatedLeft
        });
      }
          } else {
        // Fallback positioning relative to the element
        const elementRect = element.getBoundingClientRect();
        const scrollContainer = element.closest('.MuiBox-root, .MuiModal-root, .MuiPopover-root');
        const containerRect = scrollContainer?.getBoundingClientRect() || { top: 0, left: 0 };
        
        setToolbarPosition({
          top: elementRect.bottom - containerRect.top + 8,
          left: Math.max(0, elementRect.left - containerRect.left - 300)
        });
      }
  }, []);

  const hideFormattingToolbar = useCallback(() => {
    // This function is now primarily for blur events within the section
    // The main hiding logic is handled by the global click listener
    // We can keep this for compatibility but it won't do much
  }, []);

  const hideFormattingToolbarForce = useCallback(() => {
    setShowToolbar(false);
    setActiveField(null);
    activeSectionRef.current = null;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showToolbar || !activeSectionRef.current) return;

      const clickedElement = event.target as HTMLElement;
      const isInToolbar = clickedElement.closest('.formatting-toolbar');
      const isInActiveSection = activeSectionRef.current.contains(clickedElement);

      // Only hide if clicked outside both the toolbar and the active section
      if (!isInToolbar && !isInActiveSection) {
        setShowToolbar(false);
        setActiveField(null);
        activeSectionRef.current = null;
      }
    };

    if (showToolbar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showToolbar]);

  const getFormattedStyles = useCallback(() => {
    return {
      fontFamily: formatting.fontFamily,
      fontSize: `${formatting.fontSize}px`,
      fontWeight: formatting.bold ? 'bold' : 'normal',
      fontStyle: formatting.italic ? 'italic' : 'normal',
      textDecoration: formatting.underline ? 'underline' : 'none',
      textAlign: formatting.textAlign as any,
      color: formatting.textColor,
      backgroundColor: formatting.backgroundColor !== '#ffffff' ? formatting.backgroundColor : 'transparent',
    };
  }, [formatting]);

  const resetFormatting = useCallback(() => {
    const newFormatting = { ...defaultFormatting, ...initialFormatting };
    setFormatting(newFormatting);
    onFormattingChange?.(newFormatting);
  }, [initialFormatting, onFormattingChange]);

  return {
    formatting,
    showToolbar,
    toolbarPosition,
    activeField,
    
    updateFormatting,
    showFormattingToolbar,
    hideFormattingToolbar,
    hideFormattingToolbarForce,
    resetFormatting,
    
    getFormattedStyles,
    
    setFormatting,
    setShowToolbar,
    setToolbarPosition,
    setActiveField
  };
} 